import React from 'react';
import { FieldArray, Formik } from 'formik';
import { useI18n } from '@whizz/react-i18n';
import type { TopicFocusEntryInterface } from '@whizz/api-model';
import type { ToggleModalProps } from '../../../../components/modal/types';
import type { CancelTopicFocusType } from '../../../../forms/types';
import { CancelButtonModal, ConfirmButtonModal } from '../../../../global';
import { GLOBAL_ICONS } from '../../../../util/vars';
import { CANCEL_TOPIC_FOCUS_SCHEMA } from '../../../../forms/validations';
import CancelTopicTable from '../tables/CancelTopicTable';
import { Collapse } from '../../../../components';
import { ModalFooter } from '../../../../components/modal/Modal';

export interface CancelTopicModalProps extends Pick<ToggleModalProps, 'toggle'> {
  topicFocusEntries: TopicFocusEntryInterface[];
  onSubmit(values: CancelTopicFocusType): Promise<void>
}

const CancelTopicModal = ({ topicFocusEntries, onSubmit, toggle }: CancelTopicModalProps): JSX.Element => {
  const { translate } = useI18n();

  return (
    <div>
      <Formik
        initialValues={{
          cancel_students: [],
        } as CancelTopicFocusType}
        validateOnMount
        validationSchema={CANCEL_TOPIC_FOCUS_SCHEMA}
        onSubmit={(values) => {
          return Promise.resolve(onSubmit(values)).then(() => {
            toggle(false);
          });
        }}
      >
        {({ values, handleSubmit, setFieldValue, isValid, isSubmitting, isValidating }) => (
          <form onSubmit={handleSubmit}>
            <div className={'container'}>
              <FieldArray
                name={'cancel_students'}
                render={(arrayHelpers) => (
                  <CancelTopicTable
                    topicFocusEntries={topicFocusEntries}
                    selectedStudentsIds={values.cancel_students}
                    onSelect={(student) => {
                      if (values.cancel_students.includes(student)) {
                        arrayHelpers.remove(values.cancel_students.indexOf(student));
                      } else {
                        arrayHelpers.push(student);
                      }
                    }}
                    onSelectAll={(studentsIds) => {
                      if (values.cancel_students.length !== studentsIds.length) {
                        setFieldValue('cancel_students', studentsIds);
                      } else {
                        setFieldValue('cancel_students', []);
                      }
                    }}
                  />
                )}
              />
              <Collapse when={values.cancel_students.length > 0}>
                <div className={'border-t mt-md pt-md'}>
                  <p className={'font-bold mb-sm'}>
                    { 1 === values.cancel_students.length ?
                      translate('pages.TOPIC.CANCEL.MODAL.warning') :
                      translate('pages.TOPIC.CANCEL.MODAL.warning_plural')
                    }
                  </p>
                  <p>
                    {translate('pages.TOPIC.CANCEL.MODAL.description')}
                  </p>
                </div>
              </Collapse>
            </div>
            <ModalFooter>
              <CancelButtonModal
                type={'reset'}
                onClick={() => toggle(false)}
                disabled={isSubmitting || isValidating}
              />
              <ConfirmButtonModal
                type={'submit'}
                icon={GLOBAL_ICONS.delete}
                disabled={!isValid || isSubmitting || isValidating}
              >
                {values.cancel_students.length === 0 ? (
                  translate('pages.TOPIC.CANCEL.MODAL.title')
                ) : (
                  translate('pages.TOPIC.CANCEL.MODAL.confirm', {
                    count: values.cancel_students.length,
                    students: values.cancel_students.length,
                  })
                )}
              </ConfirmButtonModal>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </div>
  );
};

CancelTopicModal.displayName = 'CancelTopicModal';

export default CancelTopicModal;
